import React, { useState, useEffect, Fragment, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import CoinListTable from '../components/CoinList/CoinListTable';
import { TableProvider } from '../components/shared/table/context/TableContext';
import CoinListBulkActions from '../components/CoinList/CoinListBulkActions';
import BulkCertUpdateForm from '../components/BulkCertUpdateForm';
import { coins } from '../utils/api';

const Inventory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  const [showBulkCertUpdate, setShowBulkCertUpdate] = useState(false);
  const [certNumbers, setCertNumbers] = useState('');
  const [selectedUpdateCoins, setSelectedUpdateCoins] = useState([]);
  const [coinList, setCoinList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCoins, setSelectedCoins] = useState(() => {
    const storedSelection = localStorage.getItem('selectedCoinIds');
    if (storedSelection) {
      try {
        const parsedSelection = JSON.parse(storedSelection);
        if (Array.isArray(parsedSelection)) {
          return new Set(parsedSelection.map(String));
        }
      } catch (error) {
        console.error("Failed to parse selected coins from localStorage", error);
      }
    }
    return new Set();
  });

  useEffect(() => {
    const selectionArray = Array.from(selectedCoins);
    localStorage.setItem('selectedCoinIds', JSON.stringify(selectionArray));
  }, [selectedCoins]);
  
  const toggleCoinSelection = useCallback((coinId) => {
    setSelectedCoins(prev => {
      const newSelection = new Set(prev);
      const stringId = String(coinId);
      if (newSelection.has(stringId)) {
        newSelection.delete(stringId);
      } else {
        newSelection.add(stringId);
      }
      return newSelection;
    });
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const data = await coins.getAll({ userId });
      setCoinList(data);
    } catch (err) {
      console.error('Failed to fetch coins:', err);
      setError(err.message || 'Failed to load coins. Please try again later.');
      setCoinList([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const handleOpenBulkCertUpdate = (e) => {
      setShowBulkCertUpdate(true);
      if (e.detail?.certNumbers) {
        setCertNumbers(e.detail.certNumbers);
      }
      if (e.detail?.coins) {
        setSelectedUpdateCoins(e.detail.coins);
      }
    };
    
    document.addEventListener('openBulkCertUpdate', handleOpenBulkCertUpdate);
    return () => {
      document.removeEventListener('openBulkCertUpdate', handleOpenBulkCertUpdate);
    };
  }, []);

  return (
    <TableProvider 
      initialState={{
        loading,
        error,
        filters: []
      }}
      onFiltersClear={() => {
        setSearchParams(params => {
          params.delete('metal');
          params.delete('country');
          params.delete('denomination');
          params.delete('TPG');
          return params;
        });
      }}
      type="coin_list"
    >
      <div className="flex flex-col h-full" style={{ minHeight: '200px' }}>
        <CoinListTable
          coins={coinList}
          selectedCoins={selectedCoins}
          onToggleSelection={toggleCoinSelection}
          onSetSelection={setSelectedCoins}
          onCoinsUpdate={setCoinList}
        />
        <CoinListBulkActions
          selectedCoins={selectedCoins}
          setSelectedCoins={setSelectedCoins}
          onUpdate={fetchData}
          coinList={coinList}
        />

        {/* Bulk Cert Update Modal */}
        <Transition.Root show={showBulkCertUpdate} as={Fragment}>
          <Dialog as="div" className="relative z-10" onClose={setShowBulkCertUpdate}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-3 pb-3 pt-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-4">
                    <div className="relative">
                      <button
                        type="button"
                        className="absolute top-0 right-0 p-1 text-gray-400 hover:text-gray-500"
                        onClick={() => setShowBulkCertUpdate(false)}
                      >
                        <span className="sr-only">Close</span>
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <div>
                        <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                          Bulk Update Certification Info
                        </Dialog.Title>
                        <div className="mt-2">
                          <BulkCertUpdateForm 
                            initialCertNumbers={certNumbers}
                            selectedCoins={selectedUpdateCoins}
                            onComplete={() => {
                              setShowBulkCertUpdate(false);
                              window.location.reload(); // Refresh to show updated data
                            }} 
                          />
                        </div>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
      </div>
    </TableProvider>
  );
};

export default Inventory;
